import { ChevronDown, Phone } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { normalizeE164 } from '@/utils/phone'
import { useTelephonyMe } from '../../models/telephony'
import { useDialpad } from '../Dialpad/useDialpad'
import { type AircallNumber, NumberLabel } from './types'
import { useAircall } from './useAircall'

interface Props {
  leadId: string
  phone?: string | null
  cell?: string | null
  // When provided, these numbers are used directly (e.g. from a lead contact);
  // otherwise they're derived from the legacy phone/cell fields.
  numbers?: AircallNumber[]
  // When set (dialing a carrier contact), the resulting call activity is
  // attributed to that carrier contact.
  contactId?: string
}

function resolveNumbers(phone: string | null | undefined, cell: string | null | undefined): AircallNumber[] {
  const phoneE164 = normalizeE164(phone)
  const cellE164 = normalizeE164(cell)
  const numbers: AircallNumber[] = []
  if (phoneE164) numbers.push({ label: NumberLabel.Phone, value: phoneE164 })
  if (cellE164 && cellE164 !== phoneE164) numbers.push({ label: NumberLabel.Cell, value: cellE164 })
  return numbers
}

function DisabledCallButton({ tooltip }: { tooltip: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <Button variant="outline" disabled aria-disabled="true">
            <Phone className="size-4" />
            Call
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

export function CallButton({ leadId, phone, cell, numbers: numbersProp, contactId }: Props) {
  const aircall = useAircall()
  const dialpad = useDialpad()
  const telephonyMe = useTelephonyMe()
  const numbers = numbersProp ?? resolveNumbers(phone, cell)

  const defaultVendor = telephonyMe.data?.defaultVendor ?? null
  const useDialpadVendor = defaultVendor === 'dialpad'

  if (numbers.length === 0) {
    return <DisabledCallButton tooltip="No phone number on file" />
  }

  if (telephonyMe.isLoading) {
    return <DisabledCallButton tooltip="Loading…" />
  }

  // Vendor-specific disabled states
  if (useDialpadVendor) {
    if (!dialpad || dialpad.status === 'disabled') {
      return <DisabledCallButton tooltip="Dialpad is not enabled for your account" />
    }
    if (!dialpad.isLoggedIn) {
      return <DisabledCallButton tooltip="Connect Dialpad to make calls" />
    }
  } else {
    if (!aircall || aircall.status === 'disabled') {
      return <DisabledCallButton tooltip="Aircall is not enabled for your account" />
    }
    if (!aircall.isLoggedIn) {
      return <DisabledCallButton tooltip="Connect Aircall to make calls" />
    }
  }

  const dialNumber = (number: AircallNumber) => {
    const to = number.value
    // A per-number contactId (e.g. a carrier contact) wins over the shared prop.
    const forContact = number.contactId ?? contactId
    if (useDialpadVendor && dialpad && dialpad.status === 'ready' && dialpad.isLoggedIn) {
      return dialpad.dial({ to, leadId, contactId: forContact })
    }
    return aircall!.dial({ to, leadId, contactId: forContact, source: 'lead-detail' })
  }

  const primary = numbers[0]
  const callPrimary = () => dialNumber(primary)

  if (numbers.length === 1) {
    return (
      <Button
        variant="outline"
        className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
        onClick={callPrimary}
      >
        <Phone className="size-4" />
        Call
      </Button>
    )
  }

  return (
    <div className="inline-flex">
      <Button
        variant="outline"
        className="rounded-r-none border-r-0 border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
        onClick={callPrimary}
      >
        <Phone className="size-4" />
        Call {primary.label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            aria-label="Choose number to call"
            className="rounded-l-none border-emerald-200 bg-emerald-50 px-2 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
          >
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {numbers.map((n) => (
            <DropdownMenuItem key={n.value} onSelect={() => dialNumber(n)}>
              {n.label} · {n.value}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
